'use client';

import { styled } from '@linaria/react';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import Link from 'next/link';
import { TagsGroup } from '@fe-common-ui/Group';
import HotRate from '@fe-news/components/atoms/Member/HotRate';
import { fetchPopTopic } from '@fe-news/actions/pop-topic';
import { useQuery } from '@tanstack/react-query';
import HotPopTopicCardSkeleton from '@fe-news/components/molecules/Member/HotPopTopicCardSkeleton';
import FollowButton from '@fe-news/components/atoms/PopTopic/FollowButton';
import { GTMEventKey, sendGTM, CustomEventName } from '@fe-cnyes/fe-common-gtm';

// types
import { HotPopTopicCardProps } from '@fe-news/constants/my-track/type';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 18px 16px 16px 16px;
  border-radius: 6px;
  background-color: #f6f8fc;
  position: relative;
  font-weight: 400;
`;

const Time = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  font-size: 14px;
  letter-spacing: 0.5px;
  color: #848d97;
  margin-bottom: 16px;
`;

const Divide = styled.div`
  width: 2px;
  height: 16px;
  background-color: #e2e8f1;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  letter-spacing: 0.5px;
  color: #848d97;

  span {
    font-size: 24px;
  }
`;

const Title = styled.h3`
  flex: 1;
  margin-left: 4px;
  margin-right: 5px;

  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    -webkit-line-clamp: 2;
    line-height: 1.45;
  }

  a {
    color: #383838;
    text-decoration: none;
    font-size: 24px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.5px;

    &:hover {
      text-decoration: underline;
    }

    @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
      line-height: 1.45;
    }
  }
`;

const Summary = styled.div`
  margin-top: 16px;
  font-size: 14px;
  line-height: 1.71;
  text-align: justify;
  opacity: 0.9;
  color: #383838;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TagWrapper = styled.div`
  margin-top: 10px;
`;

const HotPopTopicCard = ({ hotRank, id }: HotPopTopicCardProps) => {
  const { data, isLoading } = useQuery({
    queryKey: ['my-pop-topic', `id:${id}`],
    queryFn: () => fetchPopTopic({ id: Number(id) })
  });

  const tags = new Set(
    [...(data?.data?.popularTopic?.tagOr ?? []), ...(data?.data?.popularTopic?.tagAnd ?? [])].filter(tag => tag !== '')
  );
  const content = data?.data?.popularTopic.content ?? '';
  const title = data?.data?.popularTopic.title ?? '';
  const time = data?.data?.popularTopic.onlineAt.split(' ')[0] ?? '';

  const onClickTitle = () => {
    sendGTM({
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_POP,
      [GTMEventKey.SECTION]: 'News_Category_熱門排行',
      [GTMEventKey.CLICK_ITEM]: `熱門追蹤時事＿${title}`
    });
  };

  const followGTMEvent = {
    [GTMEventKey.EVENT_NAME]: CustomEventName.FOLLOW_POP,
    [GTMEventKey.SECTION]: 'News_Category_熱門排行',
    [GTMEventKey.CLICK_ITEM]: `熱門追蹤時事＿${title}`
  };

  if (isLoading) {
    return <HotPopTopicCardSkeleton />;
  }

  if (title === '404') {
    return null;
  }

  return (
    <Container>
      <Time>
        <span>{time}</span>
        <Divide />
        <HotRate hotRank={hotRank} />
      </Time>
      <TitleContainer>
        <span role='img' aria-label='fire-emoji'>
          🔥
        </span>
        <Title>
          <Link href={`/poptopic/${id}`} onClick={onClickTitle}>
            {title}
          </Link>
        </Title>
        <FollowButton id={Number(id)} isAside gtmEvent={followGTMEvent} isRankingList />
      </TitleContainer>
      <Summary
        dangerouslySetInnerHTML={{
          __html: content
        }}
      />
      <TagWrapper>
        <TagsGroup tags={[...tags]} backgroundColor='rgba(255, 255, 255, 0.8)' fontColor='#515c69' />
      </TagWrapper>
    </Container>
  );
};

export default HotPopTopicCard;
